import { useQueryClient } from '@tanstack/react-query';
import ms from 'ms';
import { useEffect, useMemo } from 'react';

import { getComments } from 'hb-react/frontoffice/apps/CommentsV2/requests/getComments';
import { type CommentableType } from 'hb-react/frontoffice/apps/CommentsV2/types';
import { getQueryKey } from 'hb-react/frontoffice/apps/CommentsV2/utils/getQueryKey';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

export const usePrefetchComments = ({
  commentableId,
  commentableType,
  userId,
  showReactions = true,
}: {
  commentableId: number;
  commentableType: CommentableType;
  showReactions?: boolean;
  userId: number;
}) => {
  const queryClient = useQueryClient();
  const { flagUjAnchoringComments } = useFeatureFlag();
  const queryKey = useMemo(
    () =>
      getQueryKey({
        commentableId,
        commentableType,
      }),
    [commentableId, commentableType],
  );

  useEffect(() => {
    if (
      commentableId &&
      commentableType &&
      flagUjAnchoringComments &&
      showReactions
    ) {
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: ({ signal }) =>
          getComments({
            userId,
            params: {
              commentableId,
              commentableType,
            },
            signal,
          }),
        initialPageParam: {},
        getNextPageParam: (lastPage) =>
          lastPage.has_next_page
            ? { afterCursor: lastPage.end_cursor, focusedCommentId: undefined }
            : undefined,
        staleTime: ms('5m'),
      });
    }
  }, [
    commentableId,
    commentableType,
    flagUjAnchoringComments,
    queryClient,
    queryKey,
    showReactions,
    userId,
  ]);
};
